import { Injectable } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ConnectionService } from '@services/connection-service';
import { BroadcastService } from '@services/broadcast-service';
import { UserAddressBottomsheetComponent } from 'client/app/user-address/user-address-bottomsheet/user-address-bottomsheet.component';
import { AppConfig } from 'client/app/app.config';

const SHIPROCKET_OTP_LENGTH = 6;
const SHIPROCKET_RESEND_SECONDS = 30;

interface ShiprocketAddressData {
  phone: string;
  addressCount: number;
  expiresAt: number; // timestamp in milliseconds
}

@Injectable()
export class ShiprocketService {
  private loginToken: string = '';
  private readonly STORAGE_KEY_PREFIX = 'shiprocket_address_data_';
  private readonly EXPIRATION_HOURS = 24;
  private readonly EXPIRATION_MS = this.EXPIRATION_HOURS * 60 * 60 * 1000;

  constructor(
    private conn: ConnectionService,
    private broadcast: BroadcastService,
    private appConfig: AppConfig,
  ) {}

  /**
   * Checks if the current user is an acquisition user (not DELIVERED)
   * Shiprocket features should only be available for acquisition users
   * @returns boolean - true if user is acquisition user, false otherwise
   */
  isAcquisitionUser(): boolean {
    const user = this.conn.getCurrentUser();
    if (!user) return false;

    const orderState = user.get('orderState');
    // Acquisition users are any users who haven't reached DELIVERED state
    // Retention users are DELIVERED
    return orderState !== 'DELIVERED';
  }

  /**
   * Sends OTP to the provided mobile number (or user's mobile number if not provided)
   * Only works for acquisition users
   * @param mobileNumber - Optional mobile number (10 digits without country code)
   * @returns Promise that resolves when OTP is sent
   */
  async sendOTP(mobileNumber?: string): Promise<void> {
    if (!this.isAcquisitionUser()) return;

    const user = this.conn.getCurrentUser();
    if (!user) return;

    const phone = mobileNumber || user.get('MobileNumber');
    if (!phone) return;

    try {
      const response = await this.conn.initiateShipRocketLogin({
        countryCode: '91',
        phone,
      });
      this.loginToken = response?.token ?? '';
    } catch (error) {
      this.loginToken = '';
    }
  }

  /**
   * Handles OTP request: sends OTP and shows error if it fails
   * Only works for acquisition users
   * @param mobileNumber - Optional mobile number (10 digits without country code)
   * @returns Promise<boolean> - true if OTP was sent successfully, false otherwise
   */
  async handleOtpRequest(mobileNumber?: string): Promise<boolean> {
    if (!this.isAcquisitionUser()) return false;

    await this.sendOTP(mobileNumber);
    const success: boolean = !!this.loginToken;
    if (!success) {
      this.broadcast.broadcast('NOTIFY', { message: 'Something went wrong. Please try again.' });
    }
    return success;
  }

  /**
   * Verifies the OTP entered by the user
   * Only works for acquisition users
   * @param otp - The OTP entered by the user
   * @param mobileNumber - The mobile number used for OTP
   * @param onSuccess - Callback function called on successful verification (receives mobileNumber)
   * @returns Promise<boolean> - true if OTP was verified successfully, false otherwise
   */
  async verifyOTP(
    otp: string,
    mobileNumber: string,
    onSuccess?: (mobileNumber: string) => void | Promise<void>,
  ): Promise<boolean> {
    if (!this.isAcquisitionUser() || !this.loginToken) {
      return false;
    }

    try {
      const response = await this.conn.verifyShipRocketLoginToken({
        token: this.loginToken,
        otp,
        phone: mobileNumber,
        userAddressConsent: true,
      });

      if (!response?.success) {
        this.broadcast.broadcast('NOTIFY', { message: 'Invalid OTP. Please try again.' });
        return false;
      }

      // Call success callback if provided
      if (onSuccess) {
        await onSuccess(mobileNumber);
      }

      return true;
    } catch (error) {
      this.broadcast.broadcast('NOTIFY', { message: 'Something went wrong. Please try again.' });
      return false;
    }
  }

  /**
   * Opens the OTP bottomsheet for Shiprocket address verification.
   * Centralises the bottomsheet-opening + callback-wiring pattern so it can be
   * reused from both address-edit and review-order without duplicating logic.
   */
  openOtpBottomsheet(
    bottomSheet: MatBottomSheet,
    mobileNumber: string,
    onOtpVerified: (phone: string) => void,
  ): void {
    bottomSheet.open(UserAddressBottomsheetComponent, {
      data: {
        mode: this.appConfig.Shared.AddressBook.BottomsheetMode.Otp,
        mobileNumber,
        otpLength: SHIPROCKET_OTP_LENGTH,
        resendSeconds: SHIPROCKET_RESEND_SECONDS,
        onSendOtp: (mobile: string): Promise<boolean> => this.handleOtpRequest(mobile),
        onVerifyOtp: (otp: string, mobile: string): Promise<boolean> => {
          return this.verifyOTP(otp, mobile, async (verifiedPhone: string): Promise<void> => {
            onOtpVerified(verifiedPhone);
          });
        },
        onResendOtp: (mobile: string): Promise<boolean> => this.handleOtpRequest(mobile),
      },
    });
  }

  /**
   * Gets the current login token (useful for components that need to check if token exists)
   */
  getLoginToken(): string {
    return this.loginToken;
  }

  /**
   * Clears the login token (useful for cleanup)
   */
  clearLoginToken(): void {
    this.loginToken = '';
  }

  /**
   * Stores Shiprocket address data (phone and count) with 24-hour expiration
   * Removes any existing data for different phone numbers before storing
   * @param phone - The mobile number used for fetching addresses
   * @param addressCount - The number of addresses found
   */
  storeAddressData(phone: string, addressCount: number): void {
    // Clean up any existing data for different phone numbers
    this.clearOtherPhoneNumbers(phone);

    const storageKey = `${this.STORAGE_KEY_PREFIX}${phone}`;
    const data: ShiprocketAddressData = {
      phone,
      addressCount,
      expiresAt: Date.now() + this.EXPIRATION_MS,
    };
    try {
      localStorage.setItem(storageKey, JSON.stringify(data));
    } catch (error) {
      // Handle localStorage quota exceeded or other errors silently
      console.warn('Failed to store Shiprocket address data:', error);
    }
  }

  /**
   * Retrieves stored Shiprocket address data for a specific phone number if it exists and hasn't expired
   * @param phone - The mobile number to retrieve data for (optional, will try to find any if not provided)
   * @returns Object with phone and addressCount, or null if not found/expired
   */
  getAddressData(phone?: string): { phone: string; addressCount: number } | null {
    try {
      // If phone is provided, get data for that specific number
      if (phone) {
        const storageKey = `${this.STORAGE_KEY_PREFIX}${phone}`;
        const stored = localStorage.getItem(storageKey);
        if (!stored) return null;

        const data: ShiprocketAddressData = JSON.parse(stored);

        // Check if expired
        if (Date.now() > data.expiresAt) {
          localStorage.removeItem(storageKey);
          return null;
        }

        return {
          phone: data.phone,
          addressCount: data.addressCount,
        };
      }

      // If phone not provided, find any existing valid data
      // This is useful when we don't know which phone number was used
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.STORAGE_KEY_PREFIX)) {
          const stored = localStorage.getItem(key);
          if (!stored) continue;

          try {
            const data: ShiprocketAddressData = JSON.parse(stored);

            // Check if expired
            if (Date.now() > data.expiresAt) {
              localStorage.removeItem(key);
              continue;
            }

            return {
              phone: data.phone,
              addressCount: data.addressCount,
            };
          } catch (parseError) {
            // Handle corrupted data
            localStorage.removeItem(key);
            continue;
          }
        }
      }

      return null;
    } catch (error) {
      // Handle any errors
      return null;
    }
  }

  /**
   * Clears stored Shiprocket address data for a specific phone number
   * @param phone - The mobile number to clear data for (optional, clears all if not provided)
   */
  clearAddressData(phone?: string): void {
    if (phone) {
      const storageKey = `${this.STORAGE_KEY_PREFIX}${phone}`;
      localStorage.removeItem(storageKey);
    } else {
      // Clear all Shiprocket address data
      this.clearAllAddressData();
    }
  }

  /**
   * Clears all stored Shiprocket address data
   */
  clearAllAddressData(): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.STORAGE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key: string): void => localStorage.removeItem(key));
  }

  /**
   * Removes any existing Shiprocket address data for phone numbers other than the provided one
   * @param phone - The phone number to keep data for
   */
  private clearOtherPhoneNumbers(phone: string): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.STORAGE_KEY_PREFIX)) {
        // Extract phone number from key
        const storedPhone = key.replace(this.STORAGE_KEY_PREFIX, '');
        if (storedPhone !== phone) {
          keysToRemove.push(key);
        }
      }
    }
    keysToRemove.forEach((key: string): void => localStorage.removeItem(key));
  }
}
